import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Accordion from '../../components/Accordion/Accordion';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Curo46™ | AI-Driven Insights for Healthcare & Beyond"
        description="Discover Curo46™, General Genomics’ flagship AI platform. Learn how it leverages advanced analytics and machine learning to tackle complex challenges in healthcare, government, and commercial sectors."
      />
      <Hero
        helper="Innovation"
        headline="Explore Curo46™: Transforming data analytics with <span class='nowrap'>cutting-edge AI</span>"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Transform Healthcare with Curo46™
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how Curo46™, General Genomics&apos; groundbreaking AI
          platform, is revolutionizing healthcare, government, and commercial
          sectors. Leveraging advanced machine learning and proprietary
          datasets, Curo46™ delivers actionable insights that empower
          decision-making, optimize operations, and solve complex challenges.
          Whether detecting diseases earlier, ensuring biosecurity, or enhancing
          operational readiness, Curo46™ turns data into transformative
          outcomes. Contact us today to explore the power of tailored, real-time
          solutions designed to meet your organization&apos;s unique needs.
        </p>
        <Image
          path="/images/library/gg46.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit image="/images/library/soldiers.webp" position="right">
        <p className="helper action-on-view action-fade-in">
          Discover Features
        </p>
        <h2 className="action-on-view action-fade-in">
          How Curo46™ Empowers You
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Predictive Analytics:</strong> Leverage machine learning to
            foresee trends, enabling smarter, proactive decisions in healthcare
            and beyond.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Genomic Insights:</strong> Harness cutting-edge research to
            uncover critical connections between genetics and health outcomes.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Real-Time Dashboards:</strong> Access sophisticated
            visualizations and real-time data for immediate operational insights
            and results.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Use Cases:</strong> Drive impact with applications in
            disease detection, biosecurity, and operational readiness tailored
            to your sector.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Custom Solutions:</strong> Adapt Curo46™ to meet the unique
            demands of your organization and maximize ROI.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into our innovation, solutions, and how we serve various
          sectors with cutting-edge genomics technology.
        </p>
        <div className="card-group">
          <Card
            icon="science"
            headline="Innovation Research & Development"
            subhead="Discover the cutting-edge efforts driving the development of Curo46™ and its applications."
            btnURL="/innovation"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business_center"
            headline="Government & Commercial Solutions"
            subhead="Learn how Curo46™ is transforming key industries, from government to commercial sectors."
            btnURL="/industries/government"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/lab-technicians-man-laptop-4.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently Asked Questions About Curo46™ AI-Driven Platform
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics’ Curo46™ platform revolutionizes
          healthcare, government, and commercial sectors with actionable
          insights and advanced analytics.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What is Curo46™ and how does it work?"
          >
            Curo46™ is General Genomics’ AI-driven platform that utilizes
            proprietary datasets and machine learning models to provide advanced
            analytics and real-time insights. It addresses challenges in
            healthcare, <Link to="/industries/government">government</Link>, and{' '}
            <Link to="/industries/commercial">commercial </Link> sectors,
            offering solutions for disease detection, biosecurity, and
            operational readiness.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can Curo46™ help with healthcare and biosecurity challenges?"
          >
            Curo46™ leverages predictive analytics and genomic research to
            provide actionable insights, helping detect diseases, improve
            biosecurity measures, and enhance public health strategies. Its
            ability to process large datasets in real time makes it ideal for
            timely decision-making in critical sectors.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What specific industries benefit from using Curo46™?"
          >
            Curo46™ serves multiple sectors, including healthcare,{' '}
            <Link to="/industries/government">government</Link>, and{' '}
            <Link to="/industries/commercial">commercial </Link>
            industries. It is particularly valuable for organizations focused on
            disease detection, biosecurity, and enhancing operational readiness
            by providing tailored, data-driven solutions.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can I see Curo46™ in action before committing?"
          >
            Yes! You can reach us today to explore how Curo46™ works and see
            its features in real time. <Link to="/contact-us">Contact us</Link>{' '}
            to schedule a personalized demonstration of the platform&apos;s
            capabilities and discover how it can address your specific needs.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure continuous innovation in Curo46™?"
          >
            General Genomics invests heavily in research and development,
            driving continuous innovation in Curo46™. The platform evolves with
            advancements in AI, machine learning, and genomic research, ensuring
            it remains at the cutting edge of data analytics solutions.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
